import { IPreviewSchedulePageStore } from "sh-application/components/previewSchedulePage";
import { SchedulesViewStateStore } from "sh-application/components/schedules/lib/store/schema/SchedulesViewStateStore";

type ScheduleRequestViewState = SchedulesViewStateStore | IPreviewSchedulePageStore;

/**
 * Monotonically increasing counter shared by every updateScheduleViewDimensions invocation.
 * A module-level counter guarantees strict ordering across concurrent invocations regardless
 * of which view state, range, or data API each one targets.
 */
let requestCounter = 0;

/**
 * Records, per view state, the generation of the most recently started request. Keyed by the
 * view state object (WeakMap) so the main schedule page and the preview schedule page each track
 * their own latest request and never invalidate each other. Not stored on the MobX store on
 * purpose: this is internal control state and must not trigger observers/re-renders.
 */
let latestGenerationByViewState = new WeakMap<ScheduleRequestViewState, number>();

/**
 * Assigns a new request generation to an updateScheduleViewDimensions invocation and marks it as
 * the latest request for the given view state. Call this synchronously at the start of the
 * orchestrator, before any await, so generations are assigned in invocation order.
 *
 * During team bootstrap (and rapid view toggles) two or more invocations race: e.g. a
 * network-forced Week fetch and a cache-fast persisted-Month restore. Each awaits its data and
 * commits when it resolves. Comparing only the committed date range (the previous guard) could
 * not distinguish two same-range requests coming from different stores/APIs (Team Shifts vs My
 * Shifts). A generation token makes freshness explicit: whichever invocation started last is the
 * only one allowed to commit; every earlier in-flight fetch is superseded and skips its commit
 * and side effects, closing the load-time race that left Month view showing only the current week.
 *
 * @param viewState The schedule (or preview schedule) view state this request targets.
 * @returns The generation assigned to this invocation.
 */
export function beginScheduleRequest(viewState: ScheduleRequestViewState): number {
    const generation = ++requestCounter;
    if (viewState) {
        latestGenerationByViewState.set(viewState, generation);
    }
    return generation;
}

/**
 * Returns true when a later updateScheduleViewDimensions invocation has started for the same view
 * state, making the given generation stale. A stale invocation must skip all of its commit points
 * and post-commit side effects (data commit, clear, error surfacing, conflict recalculation) so it
 * cannot clobber the fresher request's data.
 *
 * @param viewState The schedule (or preview schedule) view state this request targets.
 * @param generation The generation assigned to this invocation by beginScheduleRequest.
 * @returns true if a newer request superseded this one; false if this is still the latest.
 */
export function isScheduleRequestSuperseded(viewState: ScheduleRequestViewState, generation: number): boolean {
    if (!viewState) {
        return false;
    }
    const latestGeneration = latestGenerationByViewState.get(viewState);
    // If no generation is recorded for this view state, nothing newer has started, so this
    // request is not superseded. Only a strictly newer (higher) recorded generation supersedes it.
    if (latestGeneration === undefined) {
        return false;
    }
    // Generations are monotonically increasing, so "superseded" means a strictly newer request
    // exists. Using `>` (rather than `!==`) states that invariant directly and avoids treating a
    // caller that passes an out-of-range/future generation as superseded when nothing newer ran.
    return latestGeneration > generation;
}

/**
 * Test-only helper that resets the module-level request counter and forgets all recorded
 * generations. The counter and WeakMap are module state shared across every invocation, which
 * makes tests order-dependent if they assert on exact generation values. Call this in a test
 * `beforeEach` to guarantee each test starts from a clean, deterministic state. Not exported for
 * production use.
 */
export function resetScheduleRequestGenerationForTests(): void {
    requestCounter = 0;
    latestGenerationByViewState = new WeakMap<ScheduleRequestViewState, number>();
}
